import { Button } from '@/components/ui/button'; // Assuming Button component exists
import { cn } from '@/lib/utils';
import { Eye, EyeOff } from 'lucide-react';
import * as React from 'react';
import { useState } from 'react';

// Extend the props to include the optional toggle prop
interface InputProps extends React.ComponentProps<'input'> {
  togglePasswordVisibility?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, togglePasswordVisibility = false, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPasswordInput = type === 'password';
    const canToggle = isPasswordInput && togglePasswordVisibility;

    const handleToggle = () => {
      setShowPassword((prev) => !prev);
    };

    return (
      <div className="relative w-full">
        <input
          type={canToggle ? (showPassword ? 'text' : 'password') : type}
          className={cn(
            'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
            canToggle && 'pr-10', // Add padding for the icon
            className
          )}
          ref={ref}
          {...props}
        />
        {canToggle && (
          <Button
            type="button" // Prevent form submission
            variant="ghost"
            size="icon"
            className="absolute inset-y-0 right-0 flex items-center justify-center h-full w-10 text-muted-foreground hover:text-foreground"
            onClick={handleToggle}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input };
